import { m } from 'framer-motion';
import { useMemo, useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import SvgIcon from '@mui/material/SvgIcon';
import Tooltip from '@mui/material/Tooltip';
import Skeleton from '@mui/material/Skeleton';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import axios, { endpoints } from 'src/utils/axios';
import { fToNow, fDateTime } from 'src/utils/format-time';
import { toPersianDigits } from 'src/utils/persian-validation';

import { varAlpha } from 'src/theme/styles';

import { Label } from 'src/components/label';
import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';
import { varHover } from 'src/components/animate';
import { Scrollbar } from 'src/components/scrollbar';
import { CustomTabs } from 'src/components/custom-tabs';

import { useAuthContext } from 'src/auth/hooks';

import { errorMessage, hasPermission } from './rado-ui';

const EVENT_META = {
  Enter: { label: 'ورود', icon: 'solar:login-3-bold-duotone', color: 'success' },
  Exit: { label: 'خروج', icon: 'solar:logout-3-bold-duotone', color: 'warning' },
  Dwell: { label: 'توقف', icon: 'solar:clock-circle-bold-duotone', color: 'secondary' },
  Speeding: { label: 'سرعت غیرمجاز', icon: 'solar:speedometer-bold-duotone', color: 'error' },
  RouteDeviation: { label: 'انحراف مسیر', icon: 'solar:route-bold-duotone', color: 'warning' },
};

const SEVERITY_META = {
  Info: { label: 'اطلاع‌رسانی', color: 'info' },
  Warning: { label: 'هشدار', color: 'warning' },
  Critical: { label: 'بحرانی', color: 'error' },
};

const fetchEvents = (status) =>
  axios
    .get(`${endpoints.fleet}/alert-events`, {
      params: { page: 1, pageSize: 30, status: status || undefined },
    })
    .then((response) => response.data);

/**
 * اعلان‌های عملیاتی ناوگان را در تمام صفحات داشبورد در دسترس قرار می‌دهد.
 */
export function RadoNotificationsDrawer({ sx, ...other }) {
  const { user } = useAuthContext();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState('new');
  const [acknowledgingId, setAcknowledgingId] = useState(null);

  const mode = user?.activeContext?.mode;
  const organizationId = user?.activeContext?.organizationId;
  const isSystemViewer = hasPermission(user, 'system.organization.view_all');
  const canView = isSystemViewer || hasPermission(user, 'alert.rule.view');
  const canAcknowledge = hasPermission(user, 'alert.rule.update');

  const newEvents = useQuery({
    queryKey: ['header-alert-events', 'new', mode, organizationId],
    queryFn: () => fetchEvents('New'),
    enabled: canView,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
    placeholderData: (previousData) => previousData,
  });

  const allEvents = useQuery({
    queryKey: ['header-alert-events', 'all', mode, organizationId],
    queryFn: () => fetchEvents(),
    enabled: canView && open && currentTab === 'all',
    refetchOnWindowFocus: true,
    placeholderData: (previousData) => previousData,
  });

  const unreadCount = newEvents.data?.totalCount ?? 0;
  const selectedQuery = currentTab === 'new' ? newEvents : allEvents;
  const items = selectedQuery.data?.items ?? [];
  const isLoading = selectedQuery.isLoading && !selectedQuery.data;
  const isRefreshing = selectedQuery.isFetching && !isLoading;

  const ariaLabel = useMemo(
    () =>
      unreadCount
        ? `اعلان‌های سامانه، ${toPersianDigits(unreadCount)} اعلان جدید`
        : 'اعلان‌های سامانه، اعلان جدیدی وجود ندارد',
    [unreadCount]
  );

  const handleOpen = useCallback(() => {
    setOpen(true);
    newEvents.refetch();
  }, [newEvents]);

  const handleRefresh = useCallback(() => {
    selectedQuery.refetch();
  }, [selectedQuery]);

  const handleAcknowledge = useCallback(
    async (eventId) => {
      setAcknowledgingId(eventId);
      try {
        await axios.post(`${endpoints.fleet}/alert-events/${eventId}/acknowledge`);
        await queryClient.invalidateQueries({ queryKey: ['header-alert-events'] });
        await queryClient.invalidateQueries({ queryKey: ['alert-events'] });
        toast.success('رسیدگی به اعلان ثبت شد.');
      } catch (requestError) {
        toast.error(errorMessage(requestError));
      } finally {
        setAcknowledgingId(null);
      }
    },
    [queryClient]
  );

  if (!canView) return null;

  return (
    <>
      <Tooltip title="اعلان‌ها">
        <IconButton
          component={m.button}
          whileTap="tap"
          whileHover="hover"
          variants={varHover(1.05)}
          aria-label={ariaLabel}
          onClick={handleOpen}
          sx={sx}
          {...other}
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <SvgIcon>
              <path
                fill="currentColor"
                d="M18.75 9v.704c0 .845.24 1.671.692 2.374l1.108 1.723c1.011 1.574.239 3.713-1.52 4.21a25.794 25.794 0 0 1-14.06 0c-1.759-.497-2.531-2.636-1.52-4.21l1.108-1.723a4.393 4.393 0 0 0 .693-2.374V9c0-3.866 3.022-7 6.749-7s6.75 3.134 6.75 7"
                opacity="0.5"
              />
              <path
                fill="currentColor"
                d="M12.75 6a.75.75 0 0 0-1.5 0v4a.75.75 0 0 0 1.5 0zM7.243 18.545a5.002 5.002 0 0 0 9.513 0c-3.145.59-6.367.59-9.513 0"
              />
            </SvgIcon>
          </Badge>
        </IconButton>
      </Tooltip>

      <Drawer
        open={open}
        anchor="right"
        onClose={() => setOpen(false)}
        slotProps={{ backdrop: { invisible: true } }}
        PaperProps={{ sx: { width: 1, maxWidth: 420 } }}
      >
        <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="h6">اعلان‌های ناوگان</Typography>
            <Typography variant="caption" color="text.secondary">
              {unreadCount
                ? `${toPersianDigits(unreadCount)} رخداد نیازمند رسیدگی`
                : 'همه اعلان‌ها بررسی شده‌اند'}
            </Typography>
          </Box>

          <Tooltip title="تازه‌سازی">
            <IconButton
              color="primary"
              aria-label="تازه‌سازی اعلان‌ها"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              {isRefreshing ? (
                <CircularProgress size={19} />
              ) : (
                <Iconify icon="solar:refresh-circle-bold-duotone" />
              )}
            </IconButton>
          </Tooltip>

          <IconButton
            aria-label="بستن اعلان‌ها"
            onClick={() => setOpen(false)}
            sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
          >
            <Iconify icon="mingcute:close-line" />
          </IconButton>
        </Stack>

        <CustomTabs
          variant="fullWidth"
          value={currentTab}
          onChange={(_, value) => setCurrentTab(value)}
        >
          <Tab
            value="new"
            label="نیازمند رسیدگی"
            iconPosition="end"
            icon={
              <Label color="error" variant={currentTab === 'new' ? 'filled' : 'soft'}>
                {toPersianDigits(unreadCount)}
              </Label>
            }
          />
          <Tab
            value="all"
            label="همه اعلان‌ها"
            iconPosition="end"
            icon={
              <Label variant={currentTab === 'all' ? 'filled' : 'soft'}>
                {toPersianDigits(allEvents.data?.totalCount ?? '—')}
              </Label>
            }
          />
        </CustomTabs>

        <Scrollbar sx={{ flex: '1 1 auto' }}>
          <Box sx={{ p: 1.5 }}>
            {isLoading && <NotificationsSkeleton />}

            {!isLoading && selectedQuery.error && (
              <Stack alignItems="center" spacing={1.25} sx={{ px: 3, py: 8, textAlign: 'center' }}>
                <Iconify
                  icon="solar:cloud-cross-bold-duotone"
                  width={48}
                  sx={{ color: 'error.main' }}
                />
                <Typography variant="subtitle1">دریافت اعلان‌ها انجام نشد</Typography>
                <Typography variant="body2" color="text.secondary">
                  اتصال خود را بررسی کنید و دوباره تلاش کنید.
                </Typography>
                <Button variant="outlined" onClick={handleRefresh}>
                  تلاش دوباره
                </Button>
              </Stack>
            )}

            {!isLoading && !selectedQuery.error && !items.length && (
              <Stack alignItems="center" spacing={1.25} sx={{ px: 3, py: 8, textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    display: 'grid',
                    borderRadius: '50%',
                    color: 'success.main',
                    placeItems: 'center',
                    bgcolor: 'success.lighter',
                  }}
                >
                  <Iconify icon="solar:bell-off-bold-duotone" width={38} />
                </Box>
                <Typography variant="subtitle1">
                  {currentTab === 'new' ? 'اعلان جدیدی ندارید' : 'هنوز اعلانی ثبت نشده است'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {currentTab === 'new'
                    ? 'رخدادهای جدید ناوگان در همین بخش نمایش داده می‌شوند.'
                    : 'پس از فعال‌شدن قوانین پایش، رخدادها اینجا قابل مشاهده‌اند.'}
                </Typography>
              </Stack>
            )}

            {!isLoading && !selectedQuery.error && (
              <Stack spacing={1}>
                {items.map((item) => (
                  <NotificationCard
                    key={item.id}
                    item={item}
                    canAcknowledge={canAcknowledge}
                    acknowledging={acknowledgingId === item.id}
                    onAcknowledge={handleAcknowledge}
                  />
                ))}
              </Stack>
            )}
          </Box>
        </Scrollbar>

        <Box sx={{ p: 1 }}>
          <Button
            fullWidth
            size="large"
            component={RouterLink}
            href={`${paths.dashboard.alertRules}?tab=events`}
            onClick={() => setOpen(false)}
          >
            مشاهده تاریخچه کامل اعلان‌ها
          </Button>
        </Box>
      </Drawer>
    </>
  );
}

function NotificationCard({ item, canAcknowledge, acknowledging, onAcknowledge }) {
  const eventMeta = EVENT_META[item.eventType] ?? EVENT_META.Enter;
  const severityMeta = SEVERITY_META[item.severity] ?? SEVERITY_META.Warning;
  const isNew = item.status === 'New';

  return (
    <Box
      sx={{
        p: 1.5,
        overflow: 'hidden',
        borderRadius: 2,
        position: 'relative',
        bgcolor: isNew
          ? (theme) => varAlpha(theme.vars.palette.primary.mainChannel, 0.045)
          : 'background.paper',
        border: (theme) =>
          `1px solid ${varAlpha(
            isNew ? theme.vars.palette.primary.mainChannel : theme.vars.palette.grey['500Channel'],
            isNew ? 0.2 : 0.12
          )}`,
        transition: (theme) => theme.transitions.create(['border-color', 'box-shadow']),
        '&:hover': {
          borderColor: (theme) => varAlpha(theme.vars.palette.primary.mainChannel, 0.34),
          boxShadow: (theme) => theme.customShadows.z4,
        },
        '&::before': {
          top: 0,
          right: 0,
          width: 4,
          height: 1,
          content: '""',
          position: 'absolute',
          bgcolor: `${severityMeta.color}.main`,
        },
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box
          sx={{
            width: 42,
            height: 42,
            display: 'grid',
            flexShrink: 0,
            borderRadius: 1.5,
            placeItems: 'center',
            color: `${eventMeta.color}.dark`,
            bgcolor: `${eventMeta.color}.lighter`,
          }}
        >
          <Iconify icon={eventMeta.icon} width={23} />
        </Box>

        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.25 }}>
            <Typography variant="subtitle2" noWrap sx={{ flexGrow: 1 }}>
              {item.deviceName}
            </Typography>
            {isNew && (
              <Box
                component="span"
                sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'error.main' }}
              />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            {item.message}
          </Typography>

          <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1.25 }}>
            <Label color={severityMeta.color} variant="soft">
              {severityMeta.label}
            </Label>
            <Label color={eventMeta.color} variant="soft">
              {eventMeta.label}
            </Label>
            <Label variant="outlined">{item.geofenceName}</Label>
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={1}
            sx={{ mt: 1.25 }}
          >
            <Tooltip title={fDateTime(item.occurredAtUtc)}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Iconify
                  icon="solar:clock-circle-linear"
                  width={16}
                  sx={{ color: 'text.disabled' }}
                />
                <Typography variant="caption" color="text.disabled">
                  {fToNow(item.occurredAtUtc)} پیش
                  {item.speed > 0 ? ` · ${toPersianDigits(item.speed)} کیلومتر/ساعت` : ''}
                </Typography>
              </Stack>
            </Tooltip>

            {isNew && canAcknowledge ? (
              <Button
                size="small"
                color="success"
                variant="soft"
                disabled={acknowledging}
                onClick={() => onAcknowledge(item.id)}
                startIcon={
                  acknowledging ? (
                    <CircularProgress size={15} color="inherit" />
                  ) : (
                    <Iconify icon="solar:check-circle-bold" width={17} />
                  )
                }
              >
                ثبت رسیدگی
              </Button>
            ) : (
              !isNew && <Label color="success">رسیدگی‌شده</Label>
            )}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}

function NotificationsSkeleton() {
  return (
    <Stack spacing={1}>
      {[0, 1, 2, 3].map((item) => (
        <Box key={item} sx={{ p: 1.5, borderRadius: 2, border: 1, borderColor: 'divider' }}>
          <Stack direction="row" spacing={1.25}>
            <Skeleton variant="rounded" width={42} height={42} />
            <Box sx={{ flexGrow: 1 }}>
              <Skeleton width="42%" />
              <Skeleton width="90%" />
              <Skeleton width="70%" />
            </Box>
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}
